import React, { useRef, useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import useBuyGetData from "./useBuyGetData";
import { Collectible, TIER_ACCENTS, STAGE_REWARDS } from "./types";
import AppContext from "../../Contexts/AppContext";
import UserContext from "../../Contexts/User";

/* ═══════════════════════════════════════════════════════════════
   GLOBAL VARS (injected once)
═══════════════════════════════════════════════════════════════ */
const GlobalVars = createGlobalStyle`
  :root {
    --line: rgba(255,255,255,.09);
    --soft: rgba(245,247,255,.76);
    --soft2: rgba(245,247,255,.54);
    --gold: #f5c842;
    --gold2: #fff0ad;
    --purple: #9d4dff;
    --cyan: #39d2ff;
    --shadow: 0 20px 60px rgba(0,0,0,.22);
    --cardGap: 18px;
    --btnH: 56px;
    --secPad: clamp(18px,3vw,30px);
    --pagePad: 14px;
  }
  @media(max-width:768px){:root{--cardGap:14px;--btnH:50px;--pagePad:12px;}}
  @media(max-width:430px){:root{--cardGap:12px;--btnH:48px;--pagePad:10px;}}
  @media(max-width:390px){:root{--pagePad:8px;}}
  @media(max-width:320px){:root{--pagePad:6px;}}
`;

/* ═══════════════════════════════════════════════════════════════
   ANIMATIONS
═══════════════════════════════════════════════════════════════ */
const shine = keyframes`0%{transform:translateX(-120%)}100%{transform:translateX(120%)}`;
const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const coinFloat = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}`;
const x2Pulse = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.05)}`;

/* ═══════════════════════════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════════════════════════ */
const Page = styled.div`
  width:100%;max-width:1320px;margin:0 auto;padding:16px var(--pagePad) 70px;
  font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  color:#f5f7ff;-webkit-font-smoothing:antialiased;
`;

const Section = styled.section`
  position:relative;margin-top:18px;padding:var(--secPad);border-radius:28px;
  border:1px solid var(--line);
  background:linear-gradient(180deg,rgba(255,255,255,.05),rgba(255,255,255,.018));
  box-shadow:var(--shadow);overflow:hidden;
  backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);
  &::before{content:"";position:absolute;inset:0;pointer-events:none;
    background:linear-gradient(180deg,rgba(255,255,255,.04),transparent 30%);}
  @media(max-width:768px){border-radius:22px;padding:18px 14px;}
  @media(max-width:430px){padding:16px 12px;border-radius:20px;}
  @media(max-width:390px){padding:14px 11px;}
  @media(max-width:320px){padding:12px 10px;border-radius:18px;}
`;

const Label = styled.div`
  display:inline-flex;align-items:center;gap:10px;margin-bottom:12px;
  color:var(--cyan);font-size:12px;line-height:1;letter-spacing:.16em;
  text-transform:uppercase;font-weight:900;position:relative;z-index:2;
  &::before{content:"";width:8px;height:8px;border-radius:50%;
    background:linear-gradient(135deg,var(--cyan),var(--purple));
    box-shadow:0 0 12px rgba(57,210,255,.45);}
`;

const Title = styled.h1`
  position:relative;z-index:2;font-size:clamp(26px,5vw,60px);line-height:1.02;
  letter-spacing:-.05em;font-weight:900;margin-bottom:10px;
`;

const Subtext = styled.p`
  position:relative;z-index:2;color:var(--soft);
  font-size:clamp(14px,1.8vw,18px);line-height:1.68;max-width:980px;font-weight:600;
`;

const HeroHead = styled.div`margin-bottom:24px;text-align:center;
  ${Subtext}{margin:0 auto;max-width:920px;}
`;

const HeroActions = styled.div`margin-top:18px;display:flex;gap:12px;flex-wrap:wrap;justify-content:center;align-items:center;`;

const LinkBtn = styled.a`
  display:inline-flex;align-items:center;justify-content:center;
  min-height:52px;padding:0 22px;border-radius:999px;
  border:1px solid rgba(255,255,255,.10);background:rgba(255,255,255,.04);
  color:#fff;font-size:14px;font-weight:900;letter-spacing:.04em;
  text-transform:uppercase;text-decoration:none;transition:transform .24s ease;
  &:hover{transform:translateY(-1px);color:#fff;}
`;

/* ── CAROUSEL ──────────────────────────────────────────── */
const CarouselArea = styled.div`
  position:relative;padding:0 56px;
  @media(max-width:1024px){padding:0 50px;}
  @media(max-width:768px){padding:0 44px;}
  @media(max-width:667px){padding:0;}
`;

const CarouselShell = styled.div`overflow:hidden;width:100%;`;

const Carousel = styled.div`
  display:flex;gap:var(--cardGap);overflow-x:auto;scroll-behavior:smooth;
  scroll-snap-type:x mandatory;padding:10px 4px 18px;
  scrollbar-width:none;-webkit-overflow-scrolling:touch;align-items:stretch;
  &::-webkit-scrollbar{display:none;}
  @media(max-width:768px){padding:8px 4px 16px;}
  @media(max-width:667px){padding:8px 12px 16px;scroll-padding-left:12px;scroll-padding-right:12px;}
  @media(max-width:430px){padding:8px 10px 16px;scroll-padding-left:10px;scroll-padding-right:10px;}
  @media(max-width:390px){padding:8px 8px 14px;scroll-padding-left:8px;scroll-padding-right:8px;}
`;

const OfferCard = styled.article<{$accent:string;$accent2:string;$glow:string;$line:string}>`
  --accent:${p=>p.$accent};--accent2:${p=>p.$accent2};
  --accentGlow:${p=>p.$glow};--accentLine:${p=>p.$line};
  flex:0 0 calc((100% - var(--cardGap)*2)/3);
  min-width:calc((100% - var(--cardGap)*2)/3);
  max-width:calc((100% - var(--cardGap)*2)/3);
  scroll-snap-align:start;display:flex;flex-direction:column;gap:14px;
  @media(max-width:1180px){flex:0 0 calc((100% - var(--cardGap))/2);min-width:calc((100% - var(--cardGap))/2);max-width:calc((100% - var(--cardGap))/2);}
  @media(max-width:768px){flex:0 0 100%;min-width:100%;max-width:100%;}
  @media(max-width:667px){flex:0 0 calc(100vw - 24px - 20px);min-width:calc(100vw - 24px - 20px);max-width:calc(100vw - 24px - 20px);}
  @media(max-width:430px){flex:0 0 calc(100vw - 20px - 24px);min-width:calc(100vw - 20px - 24px);max-width:calc(100vw - 20px - 24px);gap:10px;}
  @media(max-width:390px){flex:0 0 calc(100vw - 16px - 22px);min-width:calc(100vw - 16px - 22px);max-width:calc(100vw - 16px - 22px);}
`;

const CardFace = styled.div`
  position:relative;overflow:hidden;border-radius:28px;
  aspect-ratio:.78/1;min-height:320px;cursor:pointer;background:#101118;
  border:2px solid var(--accentLine);
  box-shadow:0 0 0 1px rgba(255,255,255,.04) inset,0 20px 42px rgba(0,0,0,.30),0 0 40px var(--accentGlow);
  &::before{content:"";position:absolute;inset:10px;border-radius:22px;border:1px solid rgba(255,255,255,.12);pointer-events:none;z-index:3;}
  &::after{content:"";position:absolute;inset:0;z-index:2;pointer-events:none;
    background:linear-gradient(180deg,rgba(8,8,12,.38) 0%,rgba(8,8,12,.10) 22%,rgba(8,8,12,0) 36%),
    linear-gradient(180deg,rgba(8,8,12,0) 42%,rgba(8,8,12,.18) 56%,rgba(8,8,12,.55) 72%,rgba(8,8,12,.97) 100%);}
  @media(max-width:768px){min-height:360px;border-radius:24px;&::before{inset:8px;border-radius:18px;}}
  @media(max-width:667px){min-height:340px;}
  @media(max-width:430px){min-height:310px;border-radius:20px;}
  @media(max-width:390px){min-height:295px;}
  @media(max-width:320px){min-height:270px;}
`;

const CardArt = styled.div`
  position:absolute;inset:0;z-index:1;background:#0d0d13;
  img{width:100%;height:100%;object-fit:cover;display:block;transform:scale(1.03);transition:transform .35s ease;filter:saturate(1.03) contrast(1.03);}
  ${CardFace}:hover & img{transform:scale(1.07);}
`;

const CardInner = styled.div`
  position:relative;z-index:4;height:100%;padding:16px;display:flex;flex-direction:column;
  @media(max-width:768px){padding:14px;}
  @media(max-width:430px){padding:12px;}
`;

const CardTopbar = styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:10px;`;

const Chip = styled.div<{$tier?:boolean}>`
  display:inline-flex;align-items:center;justify-content:center;
  min-height:34px;padding:0 13px;border-radius:999px;
  border:1px solid ${p=>p.$tier?'var(--accentLine)':'rgba(255,255,255,.16)'};
  background:rgba(8,9,14,.70);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);
  box-shadow:0 10px 22px rgba(0,0,0,.20);
  font-size:11px;line-height:1;font-weight:900;text-transform:uppercase;letter-spacing:.13em;white-space:nowrap;
  color:${p=>p.$tier?'var(--accent2)':'#fff'};
  text-shadow:-1px -1px 0 rgba(0,0,0,.6),1px -1px 0 rgba(0,0,0,.6),-1px 1px 0 rgba(0,0,0,.6),1px 1px 0 rgba(0,0,0,.6);
  @media(max-width:430px){min-height:27px;padding:0 10px;font-size:9px;}
  @media(max-width:320px){min-height:23px;padding:0 8px;font-size:7.5px;}
`;

const TagChip = styled.div<{$type:'popular'|'value'}>`
  position:absolute;top:52px;right:16px;z-index:6;
  display:inline-flex;align-items:center;justify-content:center;
  min-height:26px;padding:0 10px;border-radius:999px;border:none;
  font-size:9px;line-height:1;font-weight:900;text-transform:uppercase;letter-spacing:.13em;
  color:#111;
  background:${p=>p.$type==='popular'?'linear-gradient(135deg,#7cecff,#e0fdff)':'linear-gradient(135deg,var(--gold),var(--gold2))'};
  @media(max-width:430px){top:46px;right:12px;min-height:23px;padding:0 8px;font-size:7.5px;}
`;

const CardBottom = styled.div`margin-top:auto;display:flex;flex-direction:column;gap:8px;`;

const VcoinWrap = styled.div`display:flex;flex-direction:column;gap:2px;`;
const VcoinLabel = styled.div`
  color:#fff;font-size:clamp(11px,1vw,16px);line-height:1;font-weight:900;
  text-shadow:-2px -2px 0 rgba(0,0,0,.95),2px -2px 0 rgba(0,0,0,.95),-2px 2px 0 rgba(0,0,0,.95),2px 2px 0 rgba(0,0,0,.95);
`;
const VcoinNumber = styled.div`
  color:var(--accent2);font-size:clamp(28px,3.8vw,68px);line-height:.92;letter-spacing:-.07em;font-weight:900;
  text-shadow:-3px -3px 0 rgba(0,0,0,.97),3px -3px 0 rgba(0,0,0,.97),-3px 3px 0 rgba(0,0,0,.97),3px 3px 0 rgba(0,0,0,.97),0 8px 18px rgba(0,0,0,.50);
  @media(max-width:768px){font-size:clamp(30px,6vw,48px);}
  @media(max-width:430px){font-size:30px;}
  @media(max-width:390px){font-size:27px;}
  @media(max-width:375px){font-size:25px;}
  @media(max-width:320px){font-size:22px;}
`;

const BannerStack = styled.div`
  display:grid;gap:7px;
  @media(max-width:430px){gap:6px;}
`;
const InfoBanner = styled.div`
  min-height:52px;border-radius:16px;padding:8px 12px;display:flex;align-items:center;gap:10px;
  background:rgba(8,9,14,.74);border:1px solid rgba(255,255,255,.18);
  box-shadow:0 10px 22px rgba(0,0,0,.24);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);
  @media(max-width:768px){min-height:50px;}
  @media(max-width:430px){min-height:46px;padding:7px 10px;gap:8px;border-radius:13px;}
`;
const BannerIcon = styled.div`
  width:28px;height:28px;flex:0 0 28px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  border:1px solid var(--accentLine);background:rgba(255,255,255,.04);
  color:var(--accent2);font-size:13px;font-weight:900;box-shadow:0 0 16px var(--accentGlow);
  @media(max-width:430px){width:25px;height:25px;flex:0 0 25px;font-size:11px;}
`;
const BannerCopy = styled.div`
  display:flex;flex-direction:column;gap:3px;min-width:0;
  span{color:#fff;font-size:9px;line-height:1;text-transform:uppercase;letter-spacing:.16em;font-weight:900;
    text-shadow:-1px -1px 0 rgba(0,0,0,.75),1px -1px 0 rgba(0,0,0,.75),-1px 1px 0 rgba(0,0,0,.75),1px 1px 0 rgba(0,0,0,.75);
    @media(max-width:430px){font-size:8px;}}
  strong{color:#fff;font-size:clamp(12px,1.1vw,17px);line-height:1.1;font-weight:900;
    text-shadow:-1px -1px 0 rgba(0,0,0,.92),1px -1px 0 rgba(0,0,0,.92),-1px 1px 0 rgba(0,0,0,.92),1px 1px 0 rgba(0,0,0,.92);
    @media(max-width:430px){font-size:12px;}@media(max-width:390px){font-size:11px;}@media(max-width:320px){font-size:10px;}}
`;

const CardName = styled.div`
  color:#fff;font-size:clamp(20px,2.2vw,38px);line-height:1;letter-spacing:-.05em;font-weight:900;
  text-shadow:-2px -2px 0 rgba(0,0,0,.95),2px -2px 0 rgba(0,0,0,.95),-2px 2px 0 rgba(0,0,0,.95),2px 2px 0 rgba(0,0,0,.95);
  @media(max-width:768px){font-size:clamp(20px,4vw,30px);}
  @media(max-width:430px){font-size:20px;}@media(max-width:390px){font-size:18px;}
  @media(max-width:375px){font-size:17px;}@media(max-width:320px){font-size:15px;}
`;

/* ── OFFER META ───────────────────────────────────────── */
const OfferMeta = styled.div`display:flex;flex-direction:column;gap:10px;flex:1 1 auto;`;
const MetaStrip = styled.div`
  min-height:58px;border-radius:18px;padding:12px 14px;display:flex;align-items:center;justify-content:space-between;gap:12px;
  background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.09);
  color:var(--soft);font-size:13px;font-weight:800;line-height:1.35;
  strong{color:#fff;font-weight:900;text-align:right;}
  @media(max-width:430px){min-height:50px;font-size:12px;padding:10px 12px;}
`;
const VariantsWrap = styled.div`
  min-height:92px;border-radius:18px;padding:12px 14px;display:flex;flex-direction:column;justify-content:center;
  background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.09);
  @media(max-width:430px){padding:10px 12px;min-height:82px;}
`;
const VariantsLabel = styled.div`
  color:var(--soft2);font-size:10px;line-height:1;text-transform:uppercase;letter-spacing:.16em;font-weight:900;margin-bottom:10px;
  @media(max-width:430px){font-size:9px;margin-bottom:8px;}
`;
const Variants = styled.div`display:flex;gap:8px;flex-wrap:wrap;align-items:center;`;
const Variant = styled.button<{$active?:boolean}>`
  width:50px;height:50px;border-radius:13px;overflow:hidden;padding:0;
  border:2px solid ${p=>p.$active?'var(--accent)':'rgba(255,255,255,.12)'};
  cursor:pointer;background:#111;transition:.2s ease;box-shadow:${p=>p.$active?'0 0 0 3px var(--accentGlow)':'0 10px 18px rgba(0,0,0,.18)'};flex:0 0 auto;
  img{width:100%;height:100%;object-fit:cover;display:block;}
  @media(max-width:430px){width:44px;height:44px;border-radius:11px;}
  @media(max-width:320px){width:38px;height:38px;}
`;
const MysteryCards = styled.div`display:flex;gap:8px;align-items:center;flex-wrap:wrap;`;
const MysteryCard = styled.div`
  width:48px;height:64px;border-radius:13px;border:2px solid rgba(255,255,255,.12);
  background:linear-gradient(180deg,rgba(255,255,255,.08),rgba(255,255,255,.03)),linear-gradient(135deg,rgba(157,77,255,.26),rgba(57,210,255,.18));
  display:flex;align-items:center;justify-content:center;color:#fff;font-size:22px;font-weight:900;
  box-shadow:0 10px 18px rgba(0,0,0,.18);flex:0 0 auto;
  @media(max-width:430px){width:42px;height:58px;border-radius:11px;font-size:19px;}
  @media(max-width:320px){width:36px;height:50px;}
`;

const OfferCta = styled.button`
  width:100%;height:var(--btnH);min-height:var(--btnH);border-radius:16px;border:none;
  background:linear-gradient(135deg,var(--accent),var(--accent2));
  color:#111;font-size:15px;font-weight:900;letter-spacing:.05em;text-transform:uppercase;cursor:pointer;
  box-shadow:0 14px 28px rgba(0,0,0,.22),0 10px 28px var(--accentGlow);
  transition:transform .24s ease;display:flex;align-items:center;justify-content:center;margin-top:auto;flex-shrink:0;
  &:hover{transform:translateY(-1px);}
  @media(max-width:430px){height:48px;min-height:48px;font-size:13px;border-radius:14px;}
`;

/* ── NAV ───────────────────────────────────────────────── */
const NavBtn = styled.button<{$dir:'prev'|'next'}>`
  position:absolute;top:42%;transform:translateY(-50%);
  ${p=>p.$dir==='prev'?'left:2px;':'right:2px;'}
  width:46px;height:46px;border-radius:50%;border:1px solid rgba(255,255,255,.14);
  background:rgba(255,255,255,.07);color:#fff;font-size:28px;font-weight:700;
  display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:10;
  box-shadow:0 10px 22px rgba(0,0,0,.18);transition:background .2s;line-height:1;
  &:hover{background:rgba(255,255,255,.13);}
  @media(max-width:667px){display:none;}
`;
const DotsWrap = styled.div`display:flex;justify-content:center;gap:8px;margin-top:14px;`;
const Dot = styled.button<{$active:boolean}>`
  width:${p=>p.$active?'28px':'10px'};height:10px;border-radius:${p=>p.$active?'999px':'50%'};
  border:none;padding:0;cursor:pointer;transition:all .25s ease;
  background:${p=>p.$active?'linear-gradient(135deg,var(--gold),var(--gold2))':'rgba(255,255,255,.22)'};
`;

/* ── HERO BALANCE ─────────────────────────────────────── */
const HeroBalance = styled.div`
  margin-top:18px;padding:16px 18px;border-radius:20px;
  background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);
`;
const HeroBalanceTop = styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;
  margin-bottom:10px;font-size:13px;font-weight:800;
  span{color:var(--soft);}strong{color:#fff;font-weight:900;}
  @media(max-width:768px){flex-direction:column;align-items:flex-start;}
`;
const BarShell = styled.div`height:16px;border-radius:999px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.06);overflow:hidden;`;
const BarFill = styled.div<{$pct:number}>`
  width:${p=>p.$pct}%;height:100%;border-radius:999px;
  background:linear-gradient(90deg,#4cc9f0 0%,#7b2ff7 45%,#f5c842 100%);
  box-shadow:0 0 20px rgba(57,210,255,.28),0 0 42px rgba(157,77,255,.22),0 0 18px rgba(245,200,66,.18);
  position:relative;transition:width .8s cubic-bezier(.22,1,.36,1);overflow:hidden;
  &::before{content:"";position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,.28),transparent);animation:${shine} 2.2s linear infinite;}
`;
const BalanceCopy = styled.div`margin-top:10px;color:var(--soft);font-size:13px;line-height:1.65;font-weight:600;text-align:center;`;

/* ── GLANCE ────────────────────────────────────────────── */
const GlanceGrid = styled.div`
  display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;
  @media(max-width:1180px){grid-template-columns:repeat(2,minmax(0,1fr));}
  @media(max-width:667px){grid-template-columns:repeat(2,minmax(0,1fr));}
  @media(max-width:430px){grid-template-columns:1fr 1fr;gap:10px;}
`;
const GlanceCard = styled.div`padding:16px;border-radius:18px;background:rgba(255,255,255,.035);border:1px solid rgba(255,255,255,.08);`;
const GlanceTitle = styled.div`color:var(--soft2);font-size:11px;line-height:1;text-transform:uppercase;letter-spacing:.12em;font-weight:900;margin-bottom:8px;`;
const GlanceValue = styled.div`color:#fff;font-size:clamp(14px,1.5vw,20px);line-height:1.25;font-weight:900;`;

/* ── PROGRESS ──────────────────────────────────────────── */
const ProgressPanel = styled.div`margin-top:18px;padding:20px;border-radius:20px;background:rgba(255,255,255,.035);border:1px solid rgba(255,255,255,.08);`;
const ProgressTop = styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;
  margin-bottom:12px;font-size:14px;font-weight:800;
  @media(max-width:768px){flex-direction:column;align-items:flex-start;}
`;
const ProgressCopy = styled.div`margin-top:12px;color:var(--soft);font-size:14px;line-height:1.75;font-weight:600;`;
const StageGrid = styled.div`
  margin-top:18px;display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px;
  @media(max-width:860px){grid-template-columns:repeat(3,minmax(0,1fr));}
  @media(max-width:768px){grid-template-columns:repeat(3,minmax(0,1fr));}
  @media(max-width:430px){grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;}
  @media(max-width:375px){grid-template-columns:repeat(2,minmax(0,1fr));gap:7px;}
`;
const StageCard = styled.div<{$active:boolean}>`
  padding:14px 8px 12px;border-radius:16px;text-align:center;
  opacity:${p=>p.$active?1:.48};
  background:${p=>p.$active
    ?'linear-gradient(180deg,rgba(255,255,255,.08),rgba(255,255,255,.02)),linear-gradient(135deg,#55d7cc,#4f7cff)'
    :'linear-gradient(180deg,rgba(255,255,255,.05),rgba(255,255,255,.02)),linear-gradient(135deg,#8f7cff,#4f7cff)'};
  border:1px solid ${p=>p.$active?'rgba(245,200,66,.30)':'rgba(255,255,255,.12)'};
  box-shadow:${p=>p.$active?'0 0 34px rgba(245,200,66,.10),0 18px 34px rgba(0,0,0,.20)':'0 12px 26px rgba(0,0,0,.14)'};
`;

/* ── SIMPLE FLOW ───────────────────────────────────────── */
const SimpleGrid = styled.div`
  display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;margin-top:18px;
  @media(max-width:860px){grid-template-columns:1fr;}
`;
const SimpleCard = styled.div`
  padding:20px 18px;border-radius:20px;background:rgba(255,255,255,.035);border:1px solid rgba(255,255,255,.08);
  @media(max-width:430px){min-height:unset;padding:16px 14px;}
`;
const SimpleNum = styled.div`
  width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  margin-bottom:12px;font-size:17px;font-weight:900;color:#111;
  background:linear-gradient(135deg,var(--gold),var(--gold2));box-shadow:0 0 18px rgba(245,200,66,.18);
`;

/* ── DOUBLE V BACK ─────────────────────────────────────── */
const DoubleGrid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:28px;align-items:center;
  @media(max-width:860px){grid-template-columns:1fr;}
`;
const DoubleTitle = styled.h2`margin:0 0 10px;font-size:clamp(22px,3.5vw,42px);line-height:1.04;letter-spacing:-.045em;font-weight:900;color:var(--gold);`;
const DoubleSub = styled.p`margin:0 0 14px;color:#fff;font-size:clamp(14px,2vw,26px);line-height:1.3;letter-spacing:-.02em;font-weight:800;`;
const Steps = styled.div`display:grid;gap:10px;margin-top:16px;`;
const Step = styled.div`
  position:relative;padding:14px 14px 14px 52px;border-radius:16px;
  background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);
  color:var(--soft);font-size:14px;line-height:1.65;font-weight:600;
  strong{color:#fff;}
`;
const StepNum = styled.div`
  position:absolute;left:13px;top:13px;width:27px;height:27px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;
  background:linear-gradient(135deg,var(--gold),var(--gold2));color:#111;font-size:12px;font-weight:900;
  box-shadow:0 0 16px rgba(245,200,66,.24);
`;

/* ── COIN SCENE ────────────────────────────────────────── */
const CoinScene = styled.div`
  position:relative;width:100%;max-width:420px;aspect-ratio:1/1;margin:0 auto;
  display:flex;align-items:center;justify-content:center;
  &::before{content:"";position:absolute;inset:12%;border-radius:50%;
    background:radial-gradient(circle,rgba(245,200,66,.20) 0%,rgba(245,200,66,.10) 22%,rgba(157,77,255,.10) 45%,transparent 70%);filter:blur(18px);}
`;
const CoinLogo = styled.img`
  position:relative;z-index:3;width:155px;height:155px;object-fit:contain;
  filter:drop-shadow(0 0 22px rgba(245,200,66,.36)) drop-shadow(0 0 54px rgba(107,31,212,.16));
  animation:${coinFloat} 5s ease-in-out infinite;
  @media(max-width:430px){width:115px;height:115px;}
`;
const X2Badge = styled.div`
  position:absolute;z-index:4;top:26%;right:20%;padding:8px 14px;border-radius:12px;
  background:linear-gradient(135deg,#6320c9,#a855f7);color:#fff;font-size:24px;font-weight:900;
  letter-spacing:-.05em;box-shadow:0 0 28px rgba(168,85,247,.62),0 0 60px rgba(168,85,247,.16);
  animation:${x2Pulse} 3s ease-in-out infinite;
  @media(max-width:430px){font-size:19px;padding:6px 11px;right:14%;}
`;
const CoinLabel = styled.div`
  position:absolute;z-index:4;bottom:10%;left:50%;transform:translateX(-50%);
  padding:6px 16px;border-radius:999px;background:rgba(0,0,0,.34);
  border:1px solid rgba(245,200,66,.22);color:var(--gold);font-size:11px;font-weight:800;
  letter-spacing:.34em;text-transform:uppercase;white-space:nowrap;
  backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);
`;
const Orbit = styled.div`position:absolute;inset:0;border-radius:50%;animation:${spin} 22s linear infinite;`;
const Social = styled.div<{$bg:string}>`
  position:absolute;width:42px;height:42px;border-radius:12px;display:flex;align-items:center;justify-content:center;
  box-shadow:0 10px 28px rgba(0,0,0,.28);border:1px solid rgba(255,255,255,.10);
  backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);background:${p=>p.$bg};
  svg{width:17px;height:17px;fill:#fff;}
`;

/* ═══════════════════════════════════════════════════════════════
   SOCIAL ICON SVG PATHS
═══════════════════════════════════════════════════════════════ */
const SOCIALS = [
  { cls:"s1",bg:"linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)",d:"M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z",pos:{top:"2%",left:"50%",transform:"translateX(-50%)"}},
  { cls:"s2",bg:"#010101",d:"M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",pos:{top:"16%",right:"6%"}},
  { cls:"s3",bg:"#1877F2",d:"M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",pos:{top:"50%",right:"0",transform:"translateY(-50%)"}},
  { cls:"s4",bg:"#25D366",d:"M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z",pos:{bottom:"14%",right:"8%"}},
  { cls:"s5",bg:"#229ED9",d:"M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0h-.056zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z",pos:{bottom:"4%",left:"50%",transform:"translateX(-50%)"}},
  { cls:"s6",bg:"#0A66C2",d:"M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124zM7.119 20.452H3.555V9h3.564v11.452z",pos:{bottom:"16%",left:"6%"}},
  { cls:"s7",bg:"#ff0000",d:"M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",pos:{top:"50%",left:"0",transform:"translateY(-50%)"}},
  { cls:"s8",bg:"#000",d:"M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z",pos:{top:"16%",left:"8%"}},
];

/* ═══════════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════════ */
function fmtVcoin(n: number): string {
  if (n >= 1000) return n.toLocaleString("en-US");
  return String(n);
}

function tierClass(tier: string) {
  const t = TIER_ACCENTS[tier] ?? TIER_ACCENTS.Common;
  return { accent: t.accent, accent2: t.accent2, glow: t.glow, line: t.line };
}

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════ */
const BuyGetLanding: React.FC = () => {
  const navigate = useNavigate();
  const { collectibles, loading } = useBuyGetData();
  const { setBuyProduct } = useContext(AppContext);
  const { userInfo } = useContext(UserContext);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [dotIdx, setDotIdx] = useState(0);
  const [selectedArt, setSelectedArt] = useState<Record<string, number>>({});
  const currentStage = 1;

  /* carousel helpers */
  const getCardW = useCallback(() => {
    const el = carouselRef.current;
    if (!el || !el.children[0]) return el?.clientWidth ?? 300;
    const gap = parseFloat(getComputedStyle(el).gap) || 16;
    return (el.children[0] as HTMLElement).getBoundingClientRect().width + gap;
  }, []);

  const goTo = useCallback((i: number) => {
    const el = carouselRef.current;
    if (!el) return;
    const max = Math.max(0, collectibles.length - 1);
    const idx = Math.min(Math.max(0, i), max);
    el.scrollTo({ left: idx * getCardW(), behavior: "smooth" });
    setDotIdx(idx);
  }, [collectibles.length, getCardW]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    let t: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const w = getCardW();
        if (w > 0) setDotIdx(Math.round(el.scrollLeft / w));
      }, 80);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [getCardW]);

  const handleBuy = (item: Collectible) => {
    if (!userInfo?.uid) {
      localStorage.setItem("redirectUrl", "/buyget");
      navigate("/onekey");
      return;
    }
    const price = item.unitPrice;
    const payvalue = [price, "PRODUCT_PURCHASE", 0, 0, item.productId ?? item.id];
    localStorage.setItem("PayAmount", JSON.stringify(payvalue));
    setBuyProduct({
      id: item.productId ?? item.id,
      name: item.name,
      rarity: item.tier,
      priceUsd: item.unitPrice,
      vCoin: item.unitVcoin,
      image_link: item.images?.[0] ?? "",
      remaining: item.remainingSupply,
      totalSupply: item.totalSupply,
    });
    navigate(`/buy/${item.productId ?? item.id}`);
  };

  const reward = STAGE_REWARDS[currentStage - 1];
  const pct = ((currentStage / 15) * 100).toFixed(2);

  if (loading) {
    return <Page style={{ textAlign: "center", paddingTop: 120 }}><p style={{ color: "var(--soft)" }}>Loading collectibles...</p></Page>;
  }

  return (
    <>
      <GlobalVars />
      <Page>
        {/* ═══ HERO ═══ */}
        <Section>
          <HeroHead>
            <Label>Buy &amp; Get</Label>
            <Title>Buy Cards. Get Vcoin. Unlock Power Votes.</Title>
            <Subtext>
              Every card gives you <span style={{color:"var(--gold)"}}>Vcoin</span> plus <span style={{color:"#d7b3ff"}}>Power Votes</span> that help you move faster every time you vote.
              Use Vcoin across the ecosystem for perks, features, rewards, and <span style={{color:"#9beeff"}}>prediction participation</span>.
              Higher cards unlock stronger value, stronger momentum, and stronger upside.
            </Subtext>
          </HeroHead>

          <CarouselArea>
            <NavBtn $dir="prev" onClick={() => goTo(dotIdx - 1)}>&#x2039;</NavBtn>
            <CarouselShell>
              <Carousel ref={carouselRef}>
                {collectibles.map((c) => {
                  const t = tierClass(c.tier);
                  const artIdx = selectedArt[c.id] ?? 0;
                  const currentImg = c.images[artIdx] ?? c.images[0] ?? "/placeholder-card.png";
                  return (
                    <OfferCard key={c.id} $accent={t.accent} $accent2={t.accent2} $glow={t.glow} $line={t.line}>
                      <CardFace onClick={() => handleBuy(c)}>
                        {c.tag === "Most Popular" && <TagChip $type="popular">Most Popular</TagChip>}
                        {c.tag === "Best Value" && <TagChip $type="value">Best Value</TagChip>}
                        <CardArt><img src={currentImg} alt={c.name} /></CardArt>
                        <CardInner>
                          <CardTopbar>
                            <Chip $tier>{c.tier}</Chip>
                            <Chip>${c.unitPrice}</Chip>
                          </CardTopbar>
                          <CardBottom>
                            <div style={{display:"flex",flexDirection:"column",gap:8}}>
                              <VcoinWrap>
                                <VcoinLabel>Vcoin</VcoinLabel>
                                <VcoinNumber>{c.isMystery ? `Up to ${fmtVcoin(c.unitVcoin)}` : fmtVcoin(c.unitVcoin)}</VcoinNumber>
                              </VcoinWrap>
                              <BannerStack>
                                <InfoBanner>
                                  <BannerIcon>&#x26A1;</BannerIcon>
                                  <BannerCopy>
                                    <span>Power</span>
                                    <strong>{c.isMystery ? `Up to x${c.powerMult}` : `x${c.powerMult}`} &middot; {c.powerUses} uses</strong>
                                  </BannerCopy>
                                </InfoBanner>
                                <InfoBanner>
                                  <BannerIcon>&#x25C8;</BannerIcon>
                                  <BannerCopy>
                                    <span>Supply</span>
                                    <strong>{c.totalSupply >= 1000 ? `${(c.totalSupply / 1000).toFixed(0)}K` : c.totalSupply} &middot; {c.supplyDays} days</strong>
                                  </BannerCopy>
                                </InfoBanner>
                              </BannerStack>
                            </div>
                            <CardName>{c.name}</CardName>
                          </CardBottom>
                        </CardInner>
                      </CardFace>

                      <OfferMeta>
                        <MetaStrip><span>Card class</span><strong>{c.cardClass}</strong></MetaStrip>
                        {c.isMystery ? (
                          <VariantsWrap>
                            <VariantsLabel>Mystery Reveal</VariantsLabel>
                            <MysteryCards>
                              {[1,2,3,4,5].map(i => <MysteryCard key={i}>?</MysteryCard>)}
                            </MysteryCards>
                          </VariantsWrap>
                        ) : (
                          <VariantsWrap>
                            <VariantsLabel>Choose Card Art</VariantsLabel>
                            <Variants>
                              {c.images.map((img, i) => (
                                <Variant
                                  key={i}
                                  $active={i === artIdx}
                                  onClick={(e) => { e.stopPropagation(); setSelectedArt(prev => ({...prev, [c.id]: i})); }}
                                >
                                  <img src={img} alt="" />
                                </Variant>
                              ))}
                            </Variants>
                          </VariantsWrap>
                        )}
                        <OfferCta onClick={() => handleBuy(c)}>Buy Now</OfferCta>
                      </OfferMeta>
                    </OfferCard>
                  );
                })}
              </Carousel>
            </CarouselShell>
            <NavBtn $dir="next" onClick={() => goTo(dotIdx + 1)}>&#x203A;</NavBtn>
          </CarouselArea>

          <DotsWrap>
            {collectibles.map((_, i) => (
              <Dot key={i} $active={i === dotIdx} onClick={() => goTo(i)} />
            ))}
          </DotsWrap>

          <HeroActions>
            <LinkBtn href="https://www.votetoearn.net/vcoin" target="_blank" rel="noopener noreferrer">Read More About Vcoin</LinkBtn>
          </HeroActions>

          <HeroBalance>
            <HeroBalanceTop>
              <div><span>Current stage:</span> <strong>Stage {currentStage}</strong></div>
              <div><span>Current Vcoin Back:</span> <strong>{reward} Vcoin per $1</strong></div>
            </HeroBalanceTop>
            <BarShell><BarFill $pct={parseFloat(pct)} /></BarShell>
            <BalanceCopy>Earlier stages return more Vcoin. As stages move forward, rewards decrease.</BalanceCopy>
          </HeroBalance>
        </Section>

        {/* ═══ AT A GLANCE ═══ */}
        <Section>
          <Label>At a glance</Label>
          <GlanceGrid>
            <GlanceCard><GlanceTitle>What you buy</GlanceTitle><GlanceValue>A premium digital card</GlanceValue></GlanceCard>
            <GlanceCard><GlanceTitle>What you get</GlanceTitle><GlanceValue>Vcoin + Power Votes</GlanceValue></GlanceCard>
            <GlanceCard><GlanceTitle>Why it matters</GlanceTitle><GlanceValue>Faster progress and more utility</GlanceValue></GlanceCard>
            <GlanceCard><GlanceTitle>Use in</GlanceTitle><GlanceValue>Perks, features, rewards, prediction</GlanceValue></GlanceCard>
          </GlanceGrid>
          <ProgressPanel>
            <ProgressTop>
              <div><span style={{color:"var(--soft)"}}>Current stage:</span> <span style={{color:"var(--gold)",fontWeight:900}}>Stage {currentStage}</span></div>
              <div><span style={{color:"var(--soft)"}}>Current Vcoin Back:</span> <span style={{color:"var(--gold)",fontWeight:900}}>{reward} Vcoin per $1</span></div>
            </ProgressTop>
            <BarShell><BarFill $pct={parseFloat(pct)} /></BarShell>
            <ProgressCopy>Earlier stages return more Vcoin. As stages advance, rewards decrease. After Stage 15 is completed, the remaining Vcoin allocation can move into ecosystem growth, partnerships, and future community initiatives.</ProgressCopy>
            <StageGrid>
              {STAGE_REWARDS.map((v, i) => (
                <StageCard key={i} $active={i + 1 === currentStage}>
                  <div style={{marginBottom:6,fontSize:9,lineHeight:1,letterSpacing:".15em",textTransform:"uppercase",fontWeight:800,opacity:.9}}>Stage {i + 1}</div>
                  <div style={{fontSize:"clamp(16px,1.8vw,26px)",lineHeight:1.05,letterSpacing:"-.04em",fontWeight:900,marginBottom:4,color:"#fff6c9"}}>{v}</div>
                  <div style={{fontSize:10,lineHeight:1.4,fontWeight:700,color:"rgba(255,255,255,.92)"}}>Vcoin per $1</div>
                </StageCard>
              ))}
            </StageGrid>
          </ProgressPanel>
        </Section>

        {/* ═══ SIMPLE FLOW ═══ */}
        <Section>
          <Label>Simple flow</Label>
          <h2 style={{position:"relative",zIndex:2,fontSize:"clamp(22px,4vw,42px)",lineHeight:1.02,letterSpacing:"-.05em",fontWeight:900,marginBottom:10}}>Buy. Get. Use it.</h2>
          <Subtext>Pick your card, complete the purchase, receive Vcoin and Power Votes, and use them across the Vote To Earn ecosystem.</Subtext>
          <SimpleGrid>
            <SimpleCard><SimpleNum>1</SimpleNum><div style={{fontSize:15,lineHeight:1.2,fontWeight:900,textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}}>What you buy</div><div style={{color:"var(--soft)",fontSize:14,lineHeight:1.7,fontWeight:600}}>You buy a collectible card with a clear value level, rarity, and reward bundle.</div></SimpleCard>
            <SimpleCard><SimpleNum>2</SimpleNum><div style={{fontSize:15,lineHeight:1.2,fontWeight:900,textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}}>What you get</div><div style={{color:"var(--soft)",fontSize:14,lineHeight:1.7,fontWeight:600}}>You get Vcoin plus Power Votes. Higher cards unlock stronger value and stronger momentum.</div></SimpleCard>
            <SimpleCard><SimpleNum>3</SimpleNum><div style={{fontSize:15,lineHeight:1.2,fontWeight:900,textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}}>How to use it</div><div style={{color:"var(--soft)",fontSize:14,lineHeight:1.7,fontWeight:600}}>Use Vcoin across ecosystem features and prediction participation, and use Power Votes while voting to move faster.</div></SimpleCard>
          </SimpleGrid>
        </Section>

        {/* ═══ DOUBLE V BACK ═══ */}
        <Section>
          <DoubleGrid>
            <div>
              <Label>Double V Back</Label>
              <DoubleTitle>Boost your Vcoin rewards</DoubleTitle>
              <DoubleSub>Share your purchase and grow your upside.</DoubleSub>
              <Subtext style={{maxWidth:620}}>
                Every collectible purchase earns Vcoin through the V Back plan.
                Users with <span style={{color:"var(--gold)"}}>50+ followers</span> can share the purchase,
                send the link to our WhatsApp or official social channels, and eligible submissions can receive double rewards.
              </Subtext>
              <Steps>
                <Step><StepNum>1</StepNum><strong>BUY</strong><br/>Pick the card you want and complete the purchase.</Step>
                <Step><StepNum>2</StepNum><strong>Get Vcoin</strong><br/>Receive Vcoin Back based on the current stage reward.</Step>
                <Step><StepNum>3</StepNum><strong>Use it</strong><br/>Use your Vcoin across the Vote To Earn ecosystem.</Step>
              </Steps>
              <p style={{marginTop:14,color:"var(--soft2)",fontSize:12,fontWeight:700,lineHeight:1.7}}>Double V Back is reviewed and approved based on real activity and valid submissions. Terms and conditions apply.</p>
            </div>
            <div>
              <CoinScene>
                <Orbit>
                  {SOCIALS.map((s, i) => (
                    <Social key={i} $bg={s.bg} style={s.pos as React.CSSProperties}>
                      <svg viewBox="0 0 24 24"><path d={s.d} /></svg>
                    </Social>
                  ))}
                </Orbit>
                <CoinLogo src="https://i.ibb.co/RTRV5t2Y/Vcoin-transparent-clean.png" alt="Vcoin logo" />
                <X2Badge>x2</X2Badge>
                <CoinLabel>V Coin</CoinLabel>
              </CoinScene>
            </div>
          </DoubleGrid>
        </Section>
      </Page>
    </>
  );
};

export default BuyGetLanding;
